import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  InputAdornment,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import { postNotification } from "../../../components/utilities/Notifications";

import * as Yup from "yup";
import { Formik } from "formik";

import ImageUploader, {
  uploadBas64ToS3,
} from "../../../components/utilities/ImageUploader";
import HelpButton from "../../../components/utilities/HelpButton";
import {
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
} from "../../../components/icons/bcp/BCPIcons";
import { postData } from "../../../components/utilities/UtilityFunctions";

export default function TeamDetails(props) {
  const { team, setTeam, authContext } = props;

  const [readOnly, setReadOnly] = useState(team?.id && !team?.isOwner);

  const socialOptions = [
    { key: "Facebook", prefix: "facebook.com/" },
    { key: "Instagram", prefix: "instagram.com/" },
    { key: "Twitter", prefix: "twitter.com/" },
    { key: "Twitch", prefix: "twitch.tv/" },
    { key: "Discord" },
  ];

  return (
    <Grid container sx={{ gap: 2 }}>
      {team?.id &&
        team?.ownerId !== authContext?.authState?.identity?.userId &&
        authContext?.authState?.identity?.isAdmin === true && (
          <Grid item xs={12}>
            <Typography variant="h5">
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                onClick={() => {
                  setReadOnly(!readOnly);
                }}
                endIcon={
                  readOnly ? (
                    <BCPLockKeyhole outlined />
                  ) : (
                    <BCPLockKeyholeOpen outlined />
                  )
                }
              >
                {readOnly ? "Locked" : "Unlocked"}
              </Button>
            </Typography>
          </Grid>
        )}

      <Grid item xs={12}>
        <Formik
          initialValues={{
            name: team?.name || "",
            bio: team?.bio || "",
            websiteURL: team?.websiteURL || "",
            socials: team?.socials || {},
            notAcceptingMembers:
              team?.notAcceptingMembers !== undefined
                ? team.notAcceptingMembers
                : false,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .min(3)
              .max(
                 128
              )
              .required("Required"),
          })}
          onSubmit={async (values) => {
            try {
              var sanitizedValues = {
                ...values,
              };

              if (sanitizedValues.profileImage) {
                // upload image to s3
                let newFile = await uploadBas64ToS3(
                  sanitizedValues.profileImage
                );
                delete sanitizedValues.profileImage;
                if (newFile.fileId) {
                  sanitizedValues.profileFileId = newFile.fileId;
                }
              }
              if (sanitizedValues.bannerImage) {
                // upload image to s3
                let newFile = await uploadBas64ToS3(
                  sanitizedValues.bannerImage
                );
                delete sanitizedValues.bannerImage;
                if (newFile.fileId) {
                  sanitizedValues.bannerFileId = newFile.fileId;
                }
              }

              var apiTeam;
              if (team.id) {
                apiTeam = await postData(`/teams/${team.id}`, {
                  body: {
                    set: sanitizedValues,
                  },
                });
                setTeam({
                  ...team,
                  ...apiTeam,
                });
                postNotification("success", "Team updated");
              } else {
                sanitizedValues.ownerId =
                  authContext?.authState?.identity?.userId;
                apiTeam = await postData("/teams", {
                  body: sanitizedValues,
                });
                window.location.assign(`/organize/team/${apiTeam.id}`);
                postNotification("success", "Team Created");
              }
            } catch (err) {
              postNotification("error", err);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            touched,
            values,
            isSubmitting,
            dirty,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom>
                    Profile Picture
                  </Typography>
                  <ImageUploader
                    values={values}
                    label={values.name}
                    name="profileImage"
                    setFieldValue={setFieldValue}
                    fileId={team?.profileFile?.id}
                    defaultUrl={team?.profileFile?.url}
                    aspectW={1}
                    aspectY={1}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom>
                    Cover Photo
                  </Typography>
                  <ImageUploader
                    values={values}
                    label={values.name}
                    name="bannerImage"
                    setFieldValue={setFieldValue}
                    fileId={team?.bannerFileId}
                    defaultUrl={team?.bannerFile?.url}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    name="name"
                    value={values.name}
                    label="Name"
                    variant="outlined"
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={team?.id}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Typography className="MuiFormControlLabel-label">
                        Not Accepting Members{" "}
                        <HelpButton title="If set to true users cannot set your team as their team during player registration events, only you as the owner can add them." />
                      </Typography>
                    }
                    sx={{
                      ml: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 58px)",
                      },
                    }}
                    control={
                      <Switch
                        checked={values.notAcceptingMembers}
                        onChange={(e) => {
                          setFieldValue(
                            "notAcceptingMembers",
                            !values.notAcceptingMembers
                          );
                        }}
                        disabled={readOnly}
                      />
                    }
                    labelPlacement="start"
                  />
                </Grid>
                {socialOptions.map((socialOption, index) => {
                  return (
                    <Grid item xs={12} key={index}>
                      <TextField
                        name={`socials.${socialOption.key}`}
                        value={values.socials?.[socialOption.key]}
                        label={socialOption.key}
                        variant="outlined"
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        disabled={readOnly}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {socialOption.prefix}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(touched.bio && errors.bio)}
                    helperText={touched.bio && errors.bio}
                    name="bio"
                    value={values.bio}
                    label="Bio"
                    variant="outlined"
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    multiline
                    rows={5}
                    disabled={readOnly}
                  />
                </Grid>
                {!readOnly && (
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={readOnly || isSubmitting || !dirty}
                    >
                      {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Save"}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}
